import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface MemberList {
    fields: {
        title: string;
        description: string;
        image: string;
        pageLinkLabel: string;
        pageLink: LinkFieldValue;
        backCardPdfLink: string;
    };
}

const enum fields {
    cards = "cards",
    bannerPdfDownload = "bannerPdfDownload",
    bannerPdfLink = "bannerPdfLink",
    backCardPdfLink = "backCardPdfLink",
    pageLinkLabel = "pageLinkLabel",
    pageLink = "pageLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.cards]: MemberList[];
        [fields.bannerPdfDownload]: string;
        [fields.bannerPdfLink]: string;
        [fields.backCardPdfLink]: string;
    };
}

export interface CENlineCardStyle {
    container?: InjectableCss;
    titleText?: InjectableCss;
    description?: InjectableCss;
    pdfLabelStyle?: InjectableCss;
    sectionWrapper?: InjectableCss;
    imgWrapper?: InjectableCss;
    backCardWrapper?: InjectableCss;
    backGroundImageContainerStyles?: InjectableCss;
    bannerHeadingStyles?: InjectableCss;
    bannerDescriptionStyles?: TypographyProps;
    bannerImageStyles?: InjectableCss;
    downloadBtnStyles?: InjectableCss;
    contentContainerStyles?: InjectableCss;
    cardMainParentContainer?: InjectableCss;
    frontCardWrapper?: InjectableCss;
    cardContentContainer: InjectableCss;
    cardlinkContentContainerStyles?: InjectableCss;
    cardsWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    lineCardWrapper?: InjectableCss;
    backCardPdfLinkStyle?: InjectableCss;
    cardLinkWrapper?: InjectableCss;
}

export const exploreStyles: CENlineCardStyle = {
    bannerHeadingStyles: {
        css: css`
            font-size: 40px;
            font-weight: 800;
            line-height: 48px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 24px;
                        font-weight: 800;
                        line-height: inherit;
                    `,
                    css`
                        font-size: 24px;
                        font-weight: 800;
                        line-height: inherit;
                    `,
                    css`
                        font-size: 24px;
                        font-weight: 800;
                        line-height: inherit;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bannerDescriptionStyles: {
        variant: "p",
        css: css`
            font-size: 18px;
            font-weight: 400;
            line-height: 24px;
            margin-top: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                        font-weight: 400;
                        line-height: inherit;
                    `,
                    css`
                        font-size: 16px;
                        font-weight: 400;
                        line-height: inherit;
                    `,
                    css`
                        font-size: 16px;
                        font-weight: 400;
                        line-height: inherit;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    downloadBtnStyles: {
        css: css`
            background-color: white;
            padding: 12px 24px;
            border-radius: 24px;
            margin-top: 40px;
            > span {
                font-size: 18px;
                font-weight: 600;
                line-height: 24px;
                color: ${getColor("secondary")} !important;
            }
        `,
    },

    sectionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            justify-content: center;
            align-items: center;
            padding-bottom: 104px;
        `,
    },

    imgWrapper: {
        css: css`
            width: 270px;
            height: 200px;
            object-fit: contain;
            @media (min-width:992px) and (max-width: 1200px) {
               width: 270px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 300px;
                    `,
                    null,
                    null,
                    null,
                    css`
                        width: 300px;
                    `,
                ])}
        `,
    },

    container: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-basis: 65%;
            padding: 40px;
            span {
                [class^="TypographyStyle"] {
                    color: black;
                    font-size: 14px !important;
                    margin-bottom: 0px;
                }
            }
            @media (min-width: 992px) and (max-width: 1200px) {
                margin-top: 6px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 1px;
                        flex-basis: 80%;
                        margin-top: 20px;
                        span {
                            font-size: 12px;
                            p {
                                font-size: 10px;
                            }
                        }
                    `,
                    css`
                        margin-left: 1px;
                        flex-basis: 80%;
                        margin-top: 20px;
                        span {
                            font-size: 12px;
                            p {
                                font-size: 10px;
                            }
                        }
                    `,
                    css`
                        flex-basis: 80%;
                        span {
                            font-size: 14px;
                            p {
                                font-size: 12px;
                            }
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },

    pdfLabelStyle: {
        css: css`
            min-height: 16px;
            width: 100%;
            font-size: ${TYPOGRAPHY.size.base};
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.bold};
            cursor: pointer;
            color: #006400;
            margin-top: 16px;
            text-decoration: underline;
            font-family: "Poppins";
            a {
                span {
                    color: #006400 !important;
                }
            }
        `,
    },

    description: {
        css: css`
            font-family: "Poppins";
            font-size: 14px;
            color: ${getColor("text.main")};
            display: block;
            margin-top: 15px;
            margin-bottom: 10px;
            flex-grow: 1;
            p {
                font-size: 14px;
            }
        `,
    },

    titleText: {
        css: css`
            font-size: 18px;
            font-weight: 600;
            color: #000;
            line-height: 24px;
            margin-top: 8px;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            min-height: 48px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: auto;
                    `,
                    css`
                        min-height: auto;
                    `,
                    null,
                    null,
                    null,
            ])}
        `,
    },
    backGroundImageContainerStyles: {
        css: css`
            background: linear-gradient(90deg, #266b40 27.08%, #2d804c 71.87%, #43687a 100%);
            box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            height: auto;
            width: 100%;
            color: white;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    contentContainerStyles: {
        css: css`
            padding: 64px 0px 64px 80px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 40px;
                    `,
                    css`
                        padding: 40px 40px;
                    `,
                    css`
                        padding: 40px 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bannerImageStyles: {
        css: css`
            max-width: 554px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 330px;
                        height: auto;
                    `,
                    css`
                        max-width: 330px;
                        height: auto;
                    `,
                    css`
                        max-width: 300px;
                        height: auto;
                    `,
                    null,
                    null,
                ])}
            > img {
                width: 100%;
                object-fit: contain;
                aspect-ratio: 1/1;
                height: auto;
            }
        `,
    },
    cardMainParentContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: start;
            align-items: stretch;
            margin-top: 24px;
            margin-bottom: 24px;
        `,
    },
    backCardWrapper: {
        css: css`
            grid-area: 1 / 1;
            backface-visibility: hidden;
            background-color: #f2f2f2;
            transform: rotateY(180deg);
            box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            min-height: 380px;
        `,
    },
    frontCardWrapper: {
        css: css`
            grid-area: 1 / 1;
            background-color: #f2f2f2;
            min-height: 380px;
            display: flex;
            flex-direction: column;
        `,
    },
    cardsWrapper: {
        css: css`
            perspective: 1000px;
            width: 25%;
            padding: 0 10px;
            margin-top: 16px;
            display: flex;
            flex-direction: column;
            @media (min-width: 992px) and (max-width: 1199px) {
                width: 33%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 50%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    innerWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 1fr;
            transition: all 0.8s ease;
            transform-style: preserve-3d;
            margin-top: 16px;
            border: 3px solid #f2f2f2;
            flex-grow: 1;
        `,
    },
    cardContentContainer: {
        css: css`
            display: grid;
            grid-template-rows: auto 1fr auto; 
            flex-direction: column;
            padding: 25px;
            height: 100%;
            align-items: start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 16px;
                        grid-template-rows: auto;
                    `,
                    css`
                        padding: 16px;
                        grid-template-rows: auto;
                    `,
                    css`
                        padding: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    cardlinkContentContainerStyles: {
        css: css`
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            margin-bottom: 8px;
            margin-top: 10px;
        `,
    },
    lineCardWrapper: {
        css: css`
            color: #636161;
        `,
    },
    backCardPdfLinkStyle: {
        css: css`
            font-style: normal;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            margin-top: 16px;
            color: #0f96d9 !important;
        `,
    },
    cardLinkWrapper: {
        css: css`
            display: block;
            text-align: center;
            background: #fff;
            padding: 20px 0px;
        `,
    },
};

const styles = exploreStyles;

export const CENCommonCardBlockTile: FC<OwnProps> = ({ fields }) => {
    return (
        <>
            <StyledWrapper {...styles.cardMainParentContainer}>
                {fields.cards?.map(division => {
                    const { url } = useGetLink(division.fields.pageLink);
                    return (
                        <>
                            <StyledWrapper {...styles.cardsWrapper}>
                                <StyledWrapper {...styles.innerWrapper} className="innerWrapper">
                                    <StyledWrapper {...styles.frontCardWrapper} className="flipCard-front">
                                        <Link {...styles.cardLinkWrapper} href={url}>
                                            <Img
                                                src={division.fields.image}
                                                altText={division.fields.title}
                                                {...styles.imgWrapper}
                                            />
                                        </Link>

                                        <StyledWrapper {...styles.cardContentContainer}>
                                            <Typography as="p" {...styles.titleText}>
                                                {division.fields.title}
                                            </Typography>

                                            <Typography {...styles.description}>
                                                {parse(
                                                    transformContentHtml(division.fields.description),
                                                    parserOptions,
                                                )}
                                            </Typography>

                                            <Link href={url}>
                                                <Typography as="p" {...styles.cardlinkContentContainerStyles}>
                                                    {division.fields.pageLinkLabel}
                                                </Typography>
                                            </Link>
                                        </StyledWrapper>

                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    );
                })}
            </StyledWrapper>
        </>
    );
};
const blockCardWidget: WidgetModule = {
    component: CENCommonCardBlockTile,
    definition: {
        group: "Common",
        displayName: "CEN Card Block Tile",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "pageLinkLabel",
                        editorTemplate: "TextField",
                        displayName: "Link Label",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "pageLink",
                        displayName: "Redirection Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default blockCardWidget;
